/**
 * 平台通用类型定义（前后端共用）。
 * 约定：任何"文案"都不允许硬编码在业务代码里，一律走内容库（见 packs.ts / 后台 CRUD）。
 */

// 默认设置只保留一份真相：JSON 文件同时被 types.ts（运行时）与 scripts/gen-seed.mjs（生成种子 SQL）读取。
import defaultSettingsJson from "./content/settings.default.json";
// 游戏列表的出厂默认同样是 JSON：后台可以改（存 D1），但这份永远是可回退的基线。
import defaultGamesJson from "./content/games.json";

/** 内容分级：0 温和 / 1 暧昧 / 2 成人（18+，仅后台可导入） */
export type Tier = 0 | 1 | 2;

export interface Card {
  id: string;
  text: string;
  tags: string[];
  /** 1-3，用于卡池排序与"强度"提示 */
  intensity: number;
  /** 只有后台列表 / 导出（includeDisabled）才会带上，公开接口不下发 */
  enabled?: boolean;
}

export interface CardPack {
  id: string;
  name: string;
  tier: Tier;
  cards: Card[];
}

export interface DanmakuItem {
  id: string;
  text: string;
  tags: string[];
  intensity: number;
  /** 只有后台列表 / 导出（includeDisabled）才会带上，公开接口不下发 */
  enabled?: boolean;
  /** 同上；弹幕没有"包"的概念，档位直接挂在条目上 */
  tier?: Tier;
}

export interface CardsFile {
  version: number;
  packs: CardPack[];
}

export interface DanmakuFile {
  version: number;
  danmaku: DanmakuItem[];
}

/** games.json：新增游戏 = 加一条配置 + 一个目录 */
export interface GameConfig {
  id: string;
  name: string;
  summary: string;
  /** public/ 下的封面图路径 */
  cover: string;
  players: { min: number; max: number };
  /** 是否带 18+ 角标（该游戏涉及成人内容，需成人模式 + 年龄确认） */
  adult: boolean;
  /** 前端路由 */
  route: string;
  status: "ready" | "soon";
}

export interface GamesFile {
  version: number;
  games: GameConfig[];
}

/* ---------------------------- 首页「个人主页」配置 ---------------------------- */

/**
 * 社交链接类型：决定前台卡片上的图标与默认配色。
 * 运营方在后台只选类型 + 填账号/地址，代码里不写死任何具体账号。
 */
export type SiteLinkKind =
  | "x"
  | "telegram"
  | "qq"
  | "discord"
  | "bilibili"
  | "weibo"
  | "youtube"
  | "email"
  | "custom";

/** 首页上的一张链接卡片（参考「小苏awa的洗脑站」那种长条卡片） */
export interface SiteLink {
  id: string;
  kind: SiteLinkKind;
  /** 卡片主标题，例如「小苏的推特日常更新与碎碎念」 */
  label: string;
  /** 卡片副标题，例如「点击直达 @xiaosuawa」 */
  sublabel: string;
  /** 点击直达的地址；只允许 http(s)/mailto/tel，其它一律被服务端清空 */
  url: string;
  /** 有值则卡片变成「点击复制」（例如 QQ 群号）；可与 url 共存（优先复制） */
  copyText: string;
  /** 关掉的链接只在后台可见，不会下发给前台 */
  enabled: boolean;
}

/**
 * 展示区资料面板里的一条「作品/精选」。
 * 和 SiteLink 分开是因为语义不同：链接是「去哪儿找我」，作品是「我做过什么」。
 */
export interface SiteWork {
  id: string;
  /** 作品名 */
  title: string;
  /** 一句话说明 */
  sublabel: string;
  /** 点开地址；只允许 http(s)，留空则是一条不可点的纯展示条目 */
  url: string;
}

/** 首页底部那张「上贡」卡片（计数 +1）与「敬请期待」占位卡片 */
export interface SiteTribute {
  /** 「模拟上贡」开关 */
  enabled: boolean;
  label: string;
  sublabel: string;
  /** 「正式上贡」占位卡片：显示成"还没做完"的样子 */
  soonEnabled: boolean;
  soonLabel: string;
  soonText: string;
}

/**
 * 一张「站点自有」的图片：上传到 /api/media/<id>（R2 或 D1 存储）或直接填外链 / 站内路径。
 * 壁纸轮换用它表达「哪几张参与随机」；game.cover 之类只要一个字符串就够了。
 */
export interface SiteImage {
  id: string;
  url: string;
  /** 关掉的图只在后台可见，不下发给前台、不参与轮换 */
  enabled: boolean;
}

/* ---------------------------- 价格表（纯展示，站内不接支付） ---------------------------- */

export interface PriceCategory {
  id: string;
  name: string;
}

export interface PriceItem {
  id: string;
  /** 所属分类 id；找不到分类时归到「未分类」 */
  categoryId: string;
  /** 项目名称，例如「半身插画」 */
  name: string;
  /** 价格，字符串 —— 「50」「50 起」「面议」都要能写 */
  price: string;
  /** 单位：次 / 月 / 张 / 套… */
  unit: string;
  /** 详细说明，支持换行 */
  description: string;
  enabled: boolean;
}

/**
 * 价格表：只做信息展示。
 * 合规硬约束：站内不集成任何支付 / 打赏 / 收款，付款方式一律走「线下联系」文案。
 */
export interface SitePriceList {
  /** 是否在个人卡片里显示「价格表」入口；关闭时前台彻底看不到这一块 */
  enabled: boolean;
  /** 暂不接单：整栏显示停业提示，条目仍然保留（后台不用删） */
  closed: boolean;
  closedText: string;
  /** 价格表顶部的自定义备注 */
  note: string;
  /** 「仅展示，线下联系」说明文字 */
  contactNote: string;
  categories: PriceCategory[];
  items: PriceItem[];
}

/* ---------------------------- 游戏大厅（后台可增删改） ---------------------------- */

/**
 * 大厅里的一张游戏卡。
 * - kind = "internal"：站内路由（route 形如 /g/xxx），走前端 <Link>；
 * - kind = "external"：外链（route 是 https 地址），新窗口打开。
 * games.json 仍是「出厂默认」，后台改动存在 D1 settings.games，互不覆盖。
 */
export interface SiteGame {
  id: string;
  name: string;
  summary: string;
  cover: string;
  playersMin: number;
  playersMax: number;
  adult: boolean;
  kind: "internal" | "external";
  /** internal 是路由，external 是网址 */
  route: string;
  status: "ready" | "soon";
  enabled: boolean;
}

/** 出厂默认游戏列表（games.json）；后台没配过时用它 */
export function toSiteGame(input: unknown, index = 0): SiteGame {
  const raw = (typeof input === "object" && input !== null ? input : {}) as Record<string, unknown>;
  const players = (typeof raw.players === "object" && raw.players !== null ? raw.players : {}) as Record<
    string,
    unknown
  >;
  const str = (value: unknown, fallback: string, max: number): string =>
    typeof value === "string" && value.trim() ? value.trim().slice(0, max) : fallback;
  const num = (value: unknown, fallback: number): number =>
    typeof value === "number" && Number.isFinite(value) ? Math.max(1, Math.min(99, Math.round(value))) : fallback;
  return {
    id: str(raw.id, `game-${index + 1}`, 32),
    name: str(raw.name, `游戏 ${index + 1}`, 32),
    summary: str(raw.summary, "", 200),
    cover: str(raw.cover, "", 300),
    // 两种写法都要认：后台设置中心存的是扁平 playersMin/playersMax，
    // 出厂 games.json 用的是 players: { min, max }。只认后者会让后台改的人数被静默改成 1/2。
    playersMin: num(raw.playersMin ?? players.min, 1),
    playersMax: num(raw.playersMax ?? players.max, 2),
    adult: raw.adult === true,
    kind: raw.kind === "external" ? "external" : "internal",
    route: str(raw.route, "", 300),
    status: raw.status === "soon" ? "soon" : "ready",
    enabled: raw.enabled === undefined ? true : raw.enabled === true,
  };
}

/** games.json → SiteGame[]（出厂默认） */
export const DEFAULT_GAMES: SiteGame[] = defaultGamesJson.games.map((game, index) => toSiteGame(game, index));

/**
 * 首页（展示区 + 小游戏大厅）的全部可配置内容。
 *
 * 首屏是一块全屏「展示区」：壁纸背景（可随机轮换）+ 居中头像/立绘 + 站名 + @账号 + 签名 + 标签，
 * 轻点头像弹出底部个人卡片（详细说明 / 社交链接 / 作品 / 价格表 / 上贡）；往下滚才是小游戏区。
 */
export interface SiteProfile {
  /** 首页大标题（霓虹渐变字） */
  siteName: string;
  /** 站名下面的 @账号 / 副标题（如 @xiaosuawa）；留空则不显示 */
  handle: string;
  /** 圆形头像：上传后的 /api/media/xxx、图片外链或站点内相对路径（如 /covers/x.svg），留空用站名首字占位 */
  avatarUrl: string;
  /** 个性签名：显示在首屏站名下面（多行，最多 200 字）；留空则不显示 */
  signature: string;
  /** 个人卡片里的自我介绍（"关于我"长文本），支持换行分段；留空则不显示这一块 */
  description: string;
  /** 首屏立绘（大图，建议透明底 PNG）；留空则用圆形头像（再没有就显示站名首字） */
  illustrationUrl: string;
  /** 首屏氛围背景图（不参与轮换时的默认壁纸）；留空用 backgroundFrom → backgroundTo 渐变 */
  backgroundUrl: string;
  /** 壁纸列表：wallpaperRotate 打开时每次进首页从 enabled 的图里随机挑一张 */
  wallpapers: SiteImage[];
  wallpaperRotate: boolean;
  /** 无背景图时的渐变起止色（#rgb / #rrggbb） */
  backgroundFrom: string;
  backgroundTo: string;
  /** 主题色：立绘光晕、标签描边、按钮（#rgb / #rrggbb） */
  accentColor: string;
  /** 大标题下的多行标语，逐行渲染 */
  tagline: string[];
  /** 标签胶囊，渲染成 #白给 这种（存的时候不带 #） */
  tags: string[];
  links: SiteLink[];
  /** 资料面板里的作品/精选条目 */
  works: SiteWork[];
  tribute: SiteTribute;
  /** 价格表（纯展示，站内不接支付） */
  priceList: SitePriceList;
}

/**
 * 价格表默认值：**关闭状态**。
 * 没配过价格表的站长，前台不会出现任何价格表入口 —— 这里不预置任何具体价格/项目（也不预置分类）。
 */
export const DEFAULT_PRICE_LIST: SitePriceList = {
  enabled: false,
  closed: false,
  closedText: "最近暂不接单",
  note: "",
  contactNote: "价格表仅作信息展示，本站不提供任何在线支付；想约稿请通过上面的联系方式线下联系。",
  categories: [],
  items: [],
};

/**
 * 默认首页（SFW）：样例链接都是「平台首页」级别的地址，
 * 运营方在后台换成自己的账号即可 —— 这里不出现任何具体个人账号。
 */
export const DEFAULT_PROFILE: SiteProfile = {
  siteName: "白给小游戏合集",
  handle: "",
  avatarUrl: "",
  signature: "",
  description: "首页是站长自己的展示区：把日常碎碎念和随手做的小东西放在这里，往下滑才是小游戏。",
  illustrationUrl: "",
  backgroundUrl: "",
  wallpapers: [],
  wallpaperRotate: false,
  backgroundFrom: "#2a1050",
  backgroundTo: "#0a0512",
  accentColor: "#ff5cc8",
  tagline: ["成年人的互动小游戏合集。", "所有互动都必须双方自愿，随时可以喊停。"],
  tags: ["白给", "双方自愿", "随时可退出", "内容可配置"],
  links: [
    {
      id: "sample-x",
      kind: "x",
      label: "官方推特 / X",
      sublabel: "点击直达（后台可换成你的账号）",
      url: "https://x.com/",
      copyText: "",
      enabled: true,
    },
    {
      id: "sample-telegram",
      kind: "telegram",
      label: "Telegram 群",
      sublabel: "点击直达（后台可换成你的群）",
      url: "https://t.me/",
      copyText: "",
      enabled: true,
    },
    {
      id: "sample-qq",
      kind: "qq",
      label: "QQ 群",
      sublabel: "群号 123456（示例，后台可改）",
      url: "",
      copyText: "123456",
      enabled: true,
    },
  ],
  works: [],
  tribute: {
    enabled: true,
    label: "模拟向站长上贡",
    sublabel: "点击献上爱心",
    soonEnabled: true,
    soonLabel: "正式向站长上贡",
    soonText: "还没做完 先别点啦",
  },
  priceList: { ...DEFAULT_PRICE_LIST, categories: [], items: [] },
};

/** 后台可改的全局设置（D1 settings 表，一行一个 key） */
export interface PlatformSettings {
  /** 成人模式总开关：关闭时无论卡池怎么配，都不返回 tier=2 的内容 */
  adultMode: boolean;
  /** 年龄门开关 */
  ageGate: boolean;
  /** 是否允许房主开启"整活/必输"规则 */
  handicapAllowed: boolean;
  /** 敏感词过滤开关 */
  sensitiveFilter: boolean;
  /** 单房间弹幕每分钟上限 */
  danmakuPerMinute: number;
  /** 抽卡跳过每日次数 */
  skipDailyLimit: number;
  /** 平台公告（可为空） */
  announcement: string;
  /** 顶部滚动公告条的开关（关掉后 announcement 留着但前台不显示） */
  announcementEnabled: boolean;
  /**
   * 首页（个人主页）配置。
   * 放在这里而不是 settings.default.json 里，是因为 links[].kind 是联合类型，
   * JSON 导入只能推出 string，直接赋值给 PlatformSettings 会类型不匹配。
   */
  profile: SiteProfile;
  /** 游戏大厅列表：出厂值是 games.json，后台可增删改（存 D1） */
  games: SiteGame[];
}

export const DEFAULT_SETTINGS: PlatformSettings = {
  ...defaultSettingsJson,
  profile: DEFAULT_PROFILE,
  games: DEFAULT_GAMES.map((game) => ({ ...game })),
};

/** 抽卡请求的档位；null 表示"按用户当前档位" */
export interface DrawRequest {
  tier: Tier;
  /** 近期已出的卡（避免短时间重复），前端会话内维护，服务端也会兜底 */
  recent?: string[];
}

export interface DrawResult {
  card: Card;
  packId: string;
  /** 服务端兜底后仍未命中的情况（卡池空） */
  empty?: boolean;
}

export interface ReportPayload {
  targetType: "card" | "danmaku" | "room" | "user";
  targetId: string;
  reason: string;
  detail?: string;
}

export interface ApiError {
  error: string;
  code: string;
}

export interface StatsOverview {
  visits: number;
  draws: number;
  matches: number;
  /** 首页「模拟上贡」累计次数 */
  tributes: number;
  reports: number;
  hotCards: { id: string; text: string; count: number }[];
  hotDanmaku: { id: string; text: string; count: number }[];
  daily: { day: string; visits: number; draws: number }[];
}

/* ---------------------------- 飞行棋（双人简化版） ---------------------------- */

export type PlayerIndex = 0 | 1;

export interface PlaneOnTrack {
  where: "track";
  /** 从自己起点算起的步数 0..39，40 即到家 */
  steps: number;
}

export interface PlaneInBase {
  where: "base";
}

export interface PlaneHome {
  where: "home";
}

export type Plane = PlaneInBase | PlaneOnTrack | PlaneHome;

export interface ChessPlayer {
  planes: Plane[];
  /** 连续掷出 6 的额外回合计数 */
  extraRolls: number;
}

export type ChessPhase = "awaiting-roll" | "awaiting-move" | "finished";

export type ChessEventType =
  | "roll"
  | "move"
  | "capture"
  | "jump"
  | "launch"
  | "home"
  | "pass"
  | "win"
  | "forfeit"
  | "reveal";

export interface ChessEvent {
  type: ChessEventType;
  player: PlayerIndex;
  text: string;
  /** 事件对应的格子，用于前端高亮 */
  cell?: number;
}

export interface Handicap {
  enabled: boolean;
  /** 被"整"的一方（极难获胜），仅在结算后公开 */
  target: PlayerIndex;
  /** 0..1，越大越明显 */
  strength: number;
}

export interface ChessState {
  phase: ChessPhase;
  turn: PlayerIndex;
  dice: number | null;
  winner: PlayerIndex | null;
  players: [ChessPlayer, ChessPlayer];
  handicap: Handicap;
  /** 结算时是否已向双方公开整活规则 */
  revealed: boolean;
  events: ChessEvent[];
  /** 累计出骰数（看板/调试用） */
  rolls: number;
}

/* ---------------------------- 房间 ---------------------------- */

export interface RoomSeat {
  seat: PlayerIndex;
  name: string;
  connected: boolean;
  /** 重连凭证，仅本人可见 */
  token?: string;
}

export interface RoomPublicState {
  code: string;
  host: PlayerIndex;
  seats: [RoomSeat, RoomSeat];
  started: boolean;
  /** 双方"我同意参与"确认（双人游戏硬要求） */
  consent: [boolean, boolean];
  agreeEnd: [boolean, boolean];
  chess: ChessState | null;
  danmaku: { id: string; text: string; at: number; seat?: PlayerIndex }[];
  /** 安全词触发后的说明（结算可见） */
  endedBy: "safeword" | "agree" | "forfeit" | null;
  updatedAt: number;
}

export type RoomClientMessage =
  | { type: "hello"; name: string; token?: string }
  | { type: "consent"; accepted: boolean }
  | { type: "start"; handicap?: { enabled: boolean; target: PlayerIndex; strength: number } }
  | { type: "roll" }
  | { type: "move"; plane: number }
  | { type: "danmaku"; text?: string; itemId?: string }
  | { type: "forfeit" }
  | { type: "agree-end" }
  | { type: "safeword" }
  | { type: "ping" };

export type RoomServerMessage =
  | { type: "state"; state: RoomPublicState }
  | { type: "welcome"; state: RoomPublicState; seat: PlayerIndex }
  | { type: "error"; code: string; message: string }
  | { type: "pong" };
